import { FormEvent, useRef, useState } from 'react'
import { Message } from 'semantic-ui-react'
import ReactGA from 'react-ga4'

import RequestService from 'services/request.service'
import { getAffiliateDeckLoaded } from 'services/caching.service'

import SimplePageWrapper from 'components/misc/SimplePageWrapper'
import Link from 'components/elements/Link'
import PhatButton from 'components/formElements/PhatButton'
import Image from 'components/elements/Image'
import PhatInput from 'components/formElements/PhatInput'

import styles from './register.module.scss'
import Checkbox from 'components/formElements/Checkbox'

const RegisterPage = () => {
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [passwordOne, setPasswordOne] = useState('')
  const [passwordTwo, setPasswordTwo] = useState('')

  const [agree, setAgree] = useState(false)
  const [contactable, setContactable] = useState(false)
  const [newsletterable, setNewsletterable] = useState(false)

  const [status, setStatus] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    if (!agree) return setStatus('You must agree to our Privacy Policy and Terms of Service.')
    if (passwordOne !== passwordTwo) return setStatus('Passwords do not match.')

    setLoading(true)

    return RequestService.post('/api/rest-auth/registration/', {
      email,
      username,
      password1: passwordOne,
      password2: passwordTwo,
      profile: {
        contactable,
        newsletterable,
      },
      referredBy: getAffiliateDeckLoaded() || null,
    })
      .then(() => {
        setStatus('success')
        const referredBy = getAffiliateDeckLoaded() || 'none'

        let action = 'Created an Account'

        if (referredBy) action += ` - ref by ${referredBy}`

        ReactGA.event({ category: 'User', action })
      })
      .catch(e => {
        setStatus(Object.values(e).join('----'))
      })
      .finally(() => setLoading(false))
  }

  return (
    <SimplePageWrapper title="Archidekt signup" containerClassName={styles.page} className={styles.container}>
      <div className={styles.branding}>
        <Image alt="Archidekt basic logo" src="/images/archidekt_basic.svg" height={100} />
        Archidekt account registration
      </div>
      <form onSubmit={handleSubmit}>
        <PhatInput
          required
          value={email}
          onChange={setEmail}
          header="Email"
          type="email"
          placeholder="my_email@mail.com"
          autocomplete="email"
          selectOnFocus
        />
        <PhatInput
          value={username}
          onChange={setUsername}
          header="Username"
          type="text"
          name="username"
          placeholder="MySweetUsername"
          autocomplete="false"
          selectOnFocus
        />
        <PhatInput
          value={passwordOne}
          onChange={setPasswordOne}
          header="Password"
          type="password"
          placeholder="Password"
          autocomplete="new-password"
          selectOnFocus
        />
        <PhatInput
          value={passwordTwo}
          onChange={setPasswordTwo}
          header="Repeat password"
          type="password"
          placeholder="Password"
          autocomplete="new-password"
          selectOnFocus
        />

        <Checkbox
          checked={agree}
          onChange={() => setAgree(!agree)}
          label={
            <>
              I have read and accept the
              <Link href="/privacy" rel="noopener noreferrer" target="_blank">
                {' '}
                Privacy Policy{' '}
              </Link>
              and
              <Link href="/terms" rel="noopener noreferrer" target="_blank">
                {' '}
                Terms of Service
              </Link>
            </>
          }
        />

        <div className={styles.submitRow}>
          <PhatButton disabled={loading} type="submit" color="blue">
            Register
          </PhatButton>
        </div>

        {status === 'success' && (
          <Message positive header="Success" content="Check your email to confirm your account" />
        )}
        {status && status !== 'success' && <Message error header="Registration failed" content={status} />}

        <h3 className={styles.optionalHeader}>Optional</h3>

        <Checkbox
          checked={contactable}
          onChange={() => setContactable(!contactable)}
          label="It’s okay to email me regarding product research surveys"
        />

        <Checkbox
          checked={newsletterable}
          onChange={() => setNewsletterable(!newsletterable)}
          label="I would like to be emailed the Archidekt newsletter"
        />
      </form>
    </SimplePageWrapper>
  )
}

export default RegisterPage
